// ===============================
// VeriLink Phishing Lab - Standalone Module
// Adversarial Phishing Simulation & Explainable Detection
// All logic is deterministic, client-side, no APIs
// Does NOT modify any existing verificationLogic.js
// ===============================

// ===============================
// SHARED DATA
// ===============================

const TARGET_BRANDS = [
  { name: 'google', domain: 'google.com', display: 'Google' },
  { name: 'facebook', domain: 'facebook.com', display: 'Facebook' },
  { name: 'amazon', domain: 'amazon.com', display: 'Amazon' },
  { name: 'paypal', domain: 'paypal.com', display: 'PayPal' },
  { name: 'apple', domain: 'apple.com', display: 'Apple' },
  { name: 'microsoft', domain: 'microsoft.com', display: 'Microsoft' },
  { name: 'netflix', domain: 'netflix.com', display: 'Netflix' },
  { name: 'chase', domain: 'chase.com', display: 'Chase Bank' },
  { name: 'instagram', domain: 'instagram.com', display: 'Instagram' },
  { name: 'linkedin', domain: 'linkedin.com', display: 'LinkedIn' },
];

const CHAR_SUBSTITUTIONS = {
  a: ['@', '4', 'а', 'ɑ'],
  e: ['3', 'е', 'ε', 'ё'],
  i: ['1', '!', 'і', 'ι'],
  o: ['0', 'о', 'ο', 'ø'],
  l: ['1', '|', 'ι', 'ɭ'],
  s: ['5', '$', 'ѕ'],
  t: ['7', '+'],
  g: ['9', 'q', 'ɡ'],
  b: ['8', 'ß'],
  n: ['п', 'ɳ'],
  c: ['ϲ', 'с'],
  d: ['ԁ', 'ɗ'],
  p: ['ρ', 'р'],
  r: ['г', 'ɾ'],
  u: ['υ', 'ц'],
  w: ['ω', 'ш'],
  x: ['х', 'ж'],
  y: ['у', 'ɣ'],
};

const SUSPICIOUS_TLDS = ['.xyz', '.tk', '.ml', '.ga', '.cf', '.gq', '.top', '.loan', '.work', '.click',
  '.link', '.download', '.stream', '.win', '.bid', '.site', '.tech', '.online', '.space', '.icu', '.buzz', '.info'];

const PHISHING_KEYWORDS = ['secure', 'verify', 'login', 'update', 'account', 'confirm', 'billing',
  'support', 'service', 'alert', 'notification', 'auth', 'validate', 'recover', 'unlock'];

const PHISHING_PATHS = ['/login', '/verify', '/account/update', '/secure/auth', '/confirm-identity',
  '/billing/update', '/password-reset', '/unlock-account', '/verify-email', '/security-check'];

// ===============================
// UTILITY FUNCTIONS
// ===============================

function shannonEntropy(str) {
  if (!str || str.length === 0) return 0;
  const freq = {};
  for (const c of str) freq[c] = (freq[c] || 0) + 1;
  const len = str.length;
  return -Object.values(freq).reduce((sum, f) => {
    const p = f / len;
    return sum + p * Math.log2(p);
  }, 0);
}

function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function pick(arr, rng) {
  return arr[Math.floor(rng() * arr.length)];
}

function levenshteinDistance(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

// ===============================
// 1. ATTACKER MODE: Phishing URL Generator
// ===============================

function applyCharSubstitution(name, rng, count = 1) {
  let result = name;
  const indices = [];
  for (let i = 0; i < result.length; i++) {
    if (CHAR_SUBSTITUTIONS[result[i]]) indices.push(i);
  }
  if (indices.length === 0) return { text: result, subs: [] };
  const subs = [];
  const used = new Set();
  for (let c = 0; c < Math.min(count, indices.length); c++) {
    let idx;
    do { idx = pick(indices, rng); } while (used.has(idx) && used.size < indices.length);
    if (used.has(idx)) break;
    used.add(idx);
    const orig = result[idx];
    const replacement = pick(CHAR_SUBSTITUTIONS[orig], rng);
    subs.push({ position: idx, original: orig, replacement });
    result = result.substring(0, idx) + replacement + result.substring(idx + 1);
  }
  return { text: result, subs };
}

function applyCharOmission(name, rng) {
  if (name.length <= 3) return { text: name, position: -1 };
  const idx = 1 + Math.floor(rng() * (name.length - 2));
  return { text: name.substring(0, idx) + name.substring(idx + 1), position: idx, removed: name[idx] };
}

function applyCharDuplication(name, rng) {
  const idx = Math.floor(rng() * name.length);
  return { text: name.substring(0, idx) + name[idx] + name.substring(idx), position: idx, duplicated: name[idx] };
}

function applyCharTransposition(name, rng) {
  if (name.length < 3) return { text: name, pos1: -1, pos2: -1 };
  const idx = 1 + Math.floor(rng() * (name.length - 2));
  const arr = name.split('');
  [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
  return { text: arr.join(''), pos1: idx, pos2: idx + 1 };
}

function generateAttackerUrls(brandName, complexity = 'medium', salt) {
  const brand = TARGET_BRANDS.find(b => b.name === brandName) || TARGET_BRANDS[0];
  // Use timestamp-based salt so every call produces fresh results
  const timeSalt = salt || Date.now();
  const baseSeed = brand.name.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  const rng = seededRandom(baseSeed + timeSalt);
  const results = [];

  if (complexity === 'low') {
    // LOW: Simple typosquatting only — char swap, omission, duplication, short domains
    const sub1 = applyCharSubstitution(brand.name, rng, 1);
    results.push({
      url: `https://${sub1.text}.com/login`,
      technique: 'Typosquatting — Character Substitution',
      dangerLevel: 'low',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'Substitute character', value: `"${sub1.subs[0]?.original}" → "${sub1.subs[0]?.replacement}" at position ${sub1.subs[0]?.position}`, type: 'attack' },
        { label: 'Keep familiar TLD', value: '.com', type: 'info' },
        { label: 'Add login path', value: '/login', type: 'attack' },
        { label: 'Result', value: `${sub1.text}.com/login`, type: 'result' },
      ],
    });

    const omit = applyCharOmission(brand.name, rng);
    results.push({
      url: `https://${omit.text}.com`,
      technique: 'Typosquatting — Character Omission',
      dangerLevel: 'low',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'Remove character', value: `"${omit.removed}" at position ${omit.position}`, type: 'attack' },
        { label: 'Familiar TLD', value: '.com', type: 'info' },
        { label: 'Result', value: `${omit.text}.com`, type: 'result' },
      ],
    });

    const dup = applyCharDuplication(brand.name, rng);
    results.push({
      url: `https://${dup.text}.com`,
      technique: 'Typosquatting — Character Duplication',
      dangerLevel: 'low',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'Duplicate character', value: `"${dup.duplicated}" at position ${dup.position}`, type: 'attack' },
        { label: 'Result', value: `${dup.text}.com`, type: 'result' },
      ],
    });

    const trans = applyCharTransposition(brand.name, rng);
    results.push({
      url: `https://${trans.text}.com`,
      technique: 'Typosquatting — Character Transposition',
      dangerLevel: 'low',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'Swap adjacent characters', value: `positions ${trans.pos1}↔${trans.pos2}`, type: 'attack' },
        { label: 'Result', value: `${trans.text}.com`, type: 'result' },
      ],
    });

    return { urls: results, brand, complexity };
  }

  if (complexity === 'medium') {
    // MEDIUM: Typosquatting + phishing keywords + suspicious TLDs + brand-keyword combos
    const tld1 = pick(SUSPICIOUS_TLDS, rng);
    const kw1 = pick(PHISHING_KEYWORDS, rng);
    results.push({
      url: `https://${kw1}-${brand.name}${tld1}`,
      technique: 'Keyword Prefix + Suspicious TLD',
      dangerLevel: 'medium',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'Prepend trust keyword', value: `"${kw1}-"`, type: 'attack' },
        { label: 'Use suspicious TLD', value: tld1, type: 'attack' },
        { label: 'Result', value: `${kw1}-${brand.name}${tld1}`, type: 'result' },
      ],
    });

    const sub1 = applyCharSubstitution(brand.name, rng, 1);
    const tld2 = pick(SUSPICIOUS_TLDS, rng);
    const path1 = pick(PHISHING_PATHS, rng);
    results.push({
      url: `https://${sub1.text}-login${tld2}${path1}`,
      technique: 'Typosquatting + Keyword + Suspicious TLD',
      dangerLevel: 'medium',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'Char substitution', value: `"${sub1.subs[0]?.original}" → "${sub1.subs[0]?.replacement}"`, type: 'attack' },
        { label: 'Append keyword', value: '"-login"', type: 'attack' },
        { label: 'Suspicious TLD', value: tld2, type: 'attack' },
        { label: 'Phishing path', value: path1, type: 'attack' },
        { label: 'Result', value: `${sub1.text}-login${tld2}${path1}`, type: 'result' },
      ],
    });

    const kw2 = pick(PHISHING_KEYWORDS, rng);
    results.push({
      url: `https://${brand.name}-${kw2}.com/${pick(PHISHING_PATHS, rng).slice(1)}`,
      technique: 'Brand + Keyword Combo (.com)',
      dangerLevel: 'medium',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'Append phishing keyword', value: `"-${kw2}"`, type: 'attack' },
        { label: 'Use .com to seem legit', value: '.com', type: 'attack' },
        { label: 'Result', value: `${brand.name}-${kw2}.com`, type: 'result' },
      ],
    });

    const tld3 = pick(SUSPICIOUS_TLDS, rng);
    const kw3 = pick(PHISHING_KEYWORDS, rng);
    results.push({
      url: `https://${brand.name}.${kw3}${tld3}`,
      technique: 'Brand Subdomain + Keyword TLD',
      dangerLevel: 'medium',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'Brand as subdomain', value: `${brand.name}.`, type: 'attack' },
        { label: 'Keyword parent domain', value: kw3, type: 'attack' },
        { label: 'Suspicious TLD', value: tld3, type: 'attack' },
        { label: 'Result', value: `${brand.name}.${kw3}${tld3}`, type: 'result' },
      ],
    });

    const sub2 = applyCharSubstitution(brand.name, rng, 1);
    results.push({
      url: `http://${sub2.text}-${pick(PHISHING_KEYWORDS, rng)}${pick(SUSPICIOUS_TLDS, rng)}`,
      technique: 'No HTTPS + Typosquatting + Keyword',
      dangerLevel: 'high',
      steps: [
        { label: 'Target brand', value: brand.name, type: 'info' },
        { label: 'No HTTPS', value: 'HTTP only — no encryption', type: 'attack' },
        { label: 'Char substitution', value: `"${sub2.subs[0]?.original}" → "${sub2.subs[0]?.replacement}"`, type: 'attack' },
        { label: 'Keyword suffix + suspicious TLD', value: 'Multiple deception layers', type: 'attack' },
      ],
    });

    return { urls: results, brand, complexity };
  }

  // ADVANCED: Deep subdomain chains, brand in subdomains, numeric subs, multi-layer
  const kw1 = pick(PHISHING_KEYWORDS, rng);
  const kw2 = pick(PHISHING_KEYWORDS, rng);
  results.push({
    url: `https://${brand.name}.${kw1}.${kw2}-portal.com${pick(PHISHING_PATHS, rng)}`,
    technique: 'Deep Subdomain Impersonation',
    dangerLevel: 'critical',
    steps: [
      { label: 'Target brand', value: brand.name, type: 'info' },
      { label: 'Brand as leading subdomain', value: `${brand.name}. (appears first in URL bar)`, type: 'attack' },
      { label: 'Keyword subdomain layers', value: `${kw1}.${kw2}-portal`, type: 'attack' },
      { label: 'Real domain is hidden', value: `${kw2}-portal.com owns the domain`, type: 'info' },
      { label: 'Browser truncation exploit', value: 'Long URLs get truncated in address bar', type: 'info' },
    ],
  });

  const numBrand = brand.name.replace(/o/g, '0').replace(/i/g, '1').replace(/e/g, '3').replace(/a/g, '4').replace(/s/g, '5');
  results.push({
    url: `https://${numBrand}.${pick(PHISHING_KEYWORDS, rng)}-center${pick(SUSPICIOUS_TLDS, rng)}`,
    technique: 'Numeric Substitution Attack',
    dangerLevel: 'critical',
    steps: [
      { label: 'Target brand', value: brand.name, type: 'info' },
      { label: 'Apply numeric substitutions', value: `o→0, i→1, e→3, a→4, s→5 → "${numBrand}"`, type: 'attack' },
      { label: 'Add authority keyword', value: 'center / portal / support', type: 'attack' },
      { label: 'Suspicious TLD', value: 'Non-.com TLD hides deception', type: 'attack' },
      { label: 'Result', value: numBrand, type: 'result' },
    ],
  });

  const kw3 = pick(PHISHING_KEYWORDS, rng);
  const kw4 = pick(PHISHING_KEYWORDS, rng);
  const tld1 = pick(SUSPICIOUS_TLDS, rng);
  results.push({
    url: `https://${brand.name}-${kw3}.${kw4}.secure-portal${tld1}${pick(PHISHING_PATHS, rng)}`,
    technique: 'Multi-layer Subdomain Chain',
    dangerLevel: 'critical',
    steps: [
      { label: 'Target brand', value: brand.name, type: 'info' },
      { label: 'Brand + keyword at start', value: `${brand.name}-${kw3}`, type: 'attack' },
      { label: 'Multiple subdomain layers', value: `${kw4}.secure-portal`, type: 'attack' },
      { label: 'Suspicious TLD', value: tld1, type: 'attack' },
      { label: 'Phishing path', value: 'Credential harvesting endpoint', type: 'attack' },
    ],
  });

  const homograph = applyCharSubstitution(brand.name, seededRandom(baseSeed + timeSalt + 99), 2);
  results.push({
    url: `https://${homograph.text}.com`,
    technique: 'Homograph Attack — Unicode Look-alikes',
    dangerLevel: 'critical',
    steps: [
      { label: 'Target brand', value: brand.name, type: 'info' },
      { label: 'Concept', value: 'Replace Latin chars with visually identical Unicode (Cyrillic/Greek)', type: 'info' },
      ...homograph.subs.map(s => ({
        label: 'Unicode substitution', value: `Latin "${s.original}" → "${s.replacement}" (U+${s.replacement.charCodeAt(0).toString(16).toUpperCase()})`, type: 'attack'
      })),
      { label: 'Appears identical', value: 'Looks like the real domain in most fonts', type: 'info' },
      { label: 'Result', value: `${homograph.text}.com`, type: 'result' },
    ],
  });

  results.push({
    url: `https://xn--${brand.name.substring(0, 3)}${Math.floor(rng() * 900 + 100)}.${brand.name.substring(3)}.com`,
    technique: 'Punycode / IDN Homograph Attack',
    dangerLevel: 'critical',
    steps: [
      { label: 'Target brand', value: brand.name, type: 'info' },
      { label: 'Concept', value: 'Internationalized Domain Names use "xn--" prefix (Punycode)', type: 'info' },
      { label: 'Encode Unicode to Punycode', value: 'Browser may display the Unicode version, hiding the real domain', type: 'attack' },
      { label: 'Brand fragments in subdomains', value: 'Partial brand name creates false legitimacy', type: 'attack' },
    ],
  });

  const sub1 = applyCharSubstitution(brand.name, rng, 1);
  const kw5 = pick(PHISHING_KEYWORDS, rng);
  results.push({
    url: `http://${sub1.text}-${kw5}${pick(SUSPICIOUS_TLDS, rng)}${pick(PHISHING_PATHS, rng)}?ref=${Math.floor(rng() * 99999)}&token=a${Math.floor(rng() * 9999)}b`,
    technique: 'Multi-Vector Phishing URL',
    dangerLevel: 'critical',
    steps: [
      { label: 'Target brand', value: brand.name, type: 'info' },
      { label: 'Typosquatting', value: `"${sub1.subs[0]?.original}" → "${sub1.subs[0]?.replacement}"`, type: 'attack' },
      { label: 'Keyword stuffing', value: `"-${kw5}"`, type: 'attack' },
      { label: 'No HTTPS', value: 'HTTP only — no encryption', type: 'attack' },
      { label: 'Tracking parameters', value: 'Random query strings to track victim', type: 'attack' },
    ],
  });

  return { urls: results, brand, complexity };
}

// ===============================
// 2. DEFENDER MODE: Deep Domain Structure Analyzer
// ===============================

function analyzeDeepDomain(urlStr) {
  const tree = [];
  let totalScore = 0;

  let cleanUrl = urlStr.trim();
  if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
    cleanUrl = 'https://' + cleanUrl;
  }

  let hostname, protocol, pathname;
  try {
    const urlObj = new URL(cleanUrl);
    hostname = urlObj.hostname.toLowerCase();
    protocol = urlObj.protocol;
    pathname = urlObj.pathname;
  } catch {
    return {
      riskScore: 100,
      verdict: 'Invalid URL',
      hostname: urlStr,
      signals: [{ id: 'invalid', name: 'Invalid URL', severity: 'critical', score: 100, description: 'Could not parse URL.', children: [] }],
    };
  }

  const parts = hostname.split('.');
  const tld = '.' + (parts[parts.length - 1] || '');
  const mainDomain = parts.length >= 2 ? parts[parts.length - 2] : hostname;
  const subdomains = parts.slice(0, -2);

  // --- 1. Domain Entropy ---
  const entropy = shannonEntropy(mainDomain);
  const entropyNormal = entropy <= 3.2;
  const entropyChildren = [
    { id: 'entropy-value', name: `Shannon Entropy: ${entropy.toFixed(3)}`, severity: 'info', score: 0,
      description: `Calculated over "${mainDomain}". Normal domains: 2.0–3.2, suspicious: >3.5, random: >4.0.`, children: [] },
  ];
  if (entropy > 4.0) {
    totalScore += 20;
    entropyChildren.push({ id: 'entropy-high', name: 'Very High Entropy', severity: 'critical', score: 20,
      description: 'Entropy above 4.0 indicates randomly generated domain.', children: [] });
  } else if (entropy > 3.5) {
    totalScore += 10;
    entropyChildren.push({ id: 'entropy-elevated', name: 'Elevated Entropy', severity: 'high', score: 10,
      description: 'Entropy between 3.5–4.0 is above normal for legitimate domains.', children: [] });
  }
  tree.push({
    id: 'entropy', name: 'Domain Entropy Analysis', severity: entropy > 4.0 ? 'critical' : entropy > 3.5 ? 'high' : 'safe',
    score: entropy > 4.0 ? 20 : entropy > 3.5 ? 10 : 0,
    description: `Measures randomness in the main domain "${mainDomain}".`,
    children: entropyChildren,
  });

  // --- 2. Character Substitution Patterns ---
  const subPatterns = [];
  let subScore = 0;
  const knownBrands = TARGET_BRANDS.map(b => b.name);
  for (const brand of knownBrands) {
    if (mainDomain === brand) continue;
    const dist = levenshteinDistance(mainDomain, brand);
    if (dist > 0 && dist <= 2) {
      subScore += 25;
      const diffs = [];
      for (let i = 0; i < Math.max(mainDomain.length, brand.length); i++) {
        if (mainDomain[i] !== brand[i]) diffs.push({ pos: i, got: mainDomain[i] || '(missing)', expected: brand[i] || '(extra)' });
      }
      subPatterns.push({
        id: `sub-${brand}`, name: `Close to "${brand}" (distance: ${dist})`, severity: 'critical', score: 25,
        description: `Domain "${mainDomain}" is ${dist} edit(s) from brand "${brand}".`,
        children: diffs.map((d, i) => ({
          id: `sub-${brand}-${i}`, name: `Position ${d.pos}: "${d.got}" vs "${d.expected}"`,
          severity: CHAR_SUBSTITUTIONS[d.expected]?.includes(d.got) ? 'critical' : 'medium',
          score: CHAR_SUBSTITUTIONS[d.expected]?.includes(d.got) ? 10 : 3,
          description: CHAR_SUBSTITUTIONS[d.expected]?.includes(d.got)
            ? `Known homoglyph/substitution: "${d.expected}" → "${d.got}"`
            : `Character differs but not a known substitution pattern.`,
          children: [],
        })),
      });
    }
  }
  totalScore += Math.min(subScore, 30);
  tree.push({
    id: 'substitution', name: 'Character Substitution Patterns', severity: subScore > 0 ? 'critical' : 'safe',
    score: Math.min(subScore, 30),
    description: `Checks domain against known brands for character swap attacks.`,
    children: subPatterns.length > 0 ? subPatterns : [{
      id: 'sub-none', name: 'No substitution patterns detected', severity: 'safe', score: 0,
      description: 'Domain does not closely resemble any monitored brand.', children: [],
    }],
  });

  // --- 3. Subdomain Depth ---
  const subDepth = subdomains.length;
  let depthScore = 0;
  const depthChildren = [
    { id: 'depth-count', name: `${subDepth} subdomain level(s)`, severity: 'info', score: 0,
      description: `Subdomains: ${subdomains.length > 0 ? subdomains.join(' → ') : 'none'}`, children: [] },
  ];
  if (subDepth >= 3) {
    depthScore = 20;
    depthChildren.push({ id: 'depth-deep', name: 'Excessively Deep', severity: 'critical', score: 20,
      description: 'Three or more subdomain levels is a strong phishing indicator.', children: [] });
  } else if (subDepth === 2) {
    depthScore = 10;
    depthChildren.push({ id: 'depth-moderate', name: 'Moderate Depth', severity: 'medium', score: 10,
      description: 'Two subdomain levels is unusual for legitimate sites.', children: [] });
  }
  totalScore += depthScore;
  tree.push({
    id: 'subdomain-depth', name: 'Subdomain Depth Analysis', severity: depthScore >= 20 ? 'critical' : depthScore > 0 ? 'medium' : 'safe',
    score: depthScore, description: 'Analyzes the number of subdomain levels.',
    children: depthChildren,
  });

  // --- 4. Brand Placement ---
  const brandPlacementChildren = [];
  let brandScore = 0;
  for (const brand of knownBrands) {
    const inSubdomain = subdomains.some(s => s.includes(brand));
    const inMain = mainDomain.includes(brand) && mainDomain !== brand;
    const inPath = pathname.toLowerCase().includes(brand);
    if (inSubdomain) {
      brandScore += 20;
      brandPlacementChildren.push({
        id: `brand-sub-${brand}`, name: `"${brand}" in subdomain`, severity: 'critical', score: 20,
        description: `Brand "${brand}" appears as a subdomain instead of the main domain — classic phishing tactic.`, children: [],
      });
    }
    if (inMain && mainDomain !== brand) {
      brandScore += 10;
      brandPlacementChildren.push({
        id: `brand-main-${brand}`, name: `"${brand}" embedded in main domain`, severity: 'high', score: 10,
        description: `Brand name "${brand}" is part of "${mainDomain}" but not the actual domain.`, children: [],
      });
    }
    if (inPath) {
      brandPlacementChildren.push({
        id: `brand-path-${brand}`, name: `"${brand}" in URL path`, severity: 'medium', score: 5,
        description: `Brand referenced in path "${pathname}" to create false legitimacy.`, children: [],
      });
      brandScore += 5;
    }
  }
  totalScore += Math.min(brandScore, 25);
  tree.push({
    id: 'brand-placement', name: 'Brand Placement Analysis', severity: brandScore >= 20 ? 'critical' : brandScore > 0 ? 'high' : 'safe',
    score: Math.min(brandScore, 25),
    description: 'Checks where known brand names appear within the URL structure.',
    children: brandPlacementChildren.length > 0 ? brandPlacementChildren : [{
      id: 'brand-none', name: 'No brand misplacement detected', severity: 'safe', score: 0,
      description: 'No monitored brand names found in suspicious positions.', children: [],
    }],
  });

  // --- 5. Numeric Look-alike Substitutions ---
  const numericMap = { '0': 'o', '1': 'l/i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '9': 'g' };
  const numericFindings = [];
  let numScore = 0;
  for (const [digit, letters] of Object.entries(numericMap)) {
    if (mainDomain.includes(digit)) {
      numScore += 8;
      numericFindings.push({
        id: `num-${digit}`, name: `Digit "${digit}" may replace "${letters}"`, severity: 'high', score: 8,
        description: `Found "${digit}" in "${mainDomain}" — common substitution for "${letters}".`, children: [],
      });
    }
  }
  totalScore += Math.min(numScore, 20);
  tree.push({
    id: 'numeric', name: 'Numeric Look-alike Detection', severity: numScore > 0 ? 'high' : 'safe',
    score: Math.min(numScore, 20),
    description: 'Detects digits used as visual substitutes for letters.',
    children: numericFindings.length > 0 ? numericFindings : [{
      id: 'num-none', name: 'No numeric substitutions found', severity: 'safe', score: 0,
      description: 'Domain does not contain suspicious digit-for-letter replacements.', children: [],
    }],
  });

  // --- 6. Suspicious Keyword Density ---
  const fullDomain = hostname + pathname;
  const foundKeywords = PHISHING_KEYWORDS.filter(kw => fullDomain.includes(kw));
  let kwScore = foundKeywords.length * 5;
  totalScore += Math.min(kwScore, 15);
  tree.push({
    id: 'keywords', name: 'Suspicious Keyword Density', severity: foundKeywords.length >= 3 ? 'critical' : foundKeywords.length > 0 ? 'medium' : 'safe',
    score: Math.min(kwScore, 15),
    description: `Found ${foundKeywords.length} phishing-associated keywords in URL.`,
    children: foundKeywords.length > 0
      ? foundKeywords.map(kw => ({
          id: `kw-${kw}`, name: `"${kw}" detected`, severity: 'medium', score: 5,
          description: `Keyword "${kw}" is commonly used in phishing URLs to build false trust.`, children: [],
        }))
      : [{ id: 'kw-none', name: 'No suspicious keywords', severity: 'safe', score: 0, description: 'Clean of known phishing keywords.', children: [] }],
  });

  // --- 7. Domain Length Anomaly ---
  const domainLen = hostname.length;
  let lenScore = 0;
  const lenChildren = [
    { id: 'len-value', name: `Domain length: ${domainLen} characters`, severity: 'info', score: 0,
      description: `Normal: 5–25 characters. Suspicious: >30. Very suspicious: >40.`, children: [] },
  ];
  if (domainLen > 40) {
    lenScore = 15;
    lenChildren.push({ id: 'len-extreme', name: 'Extremely Long Domain', severity: 'critical', score: 15,
      description: 'Domains over 40 chars are almost always malicious.', children: [] });
  } else if (domainLen > 30) {
    lenScore = 8;
    lenChildren.push({ id: 'len-long', name: 'Unusually Long Domain', severity: 'medium', score: 8,
      description: 'Domains over 30 chars warrant closer inspection.', children: [] });
  }
  totalScore += lenScore;
  tree.push({
    id: 'length', name: 'Domain Length Analysis', severity: lenScore >= 15 ? 'critical' : lenScore > 0 ? 'medium' : 'safe',
    score: lenScore, description: 'Checks if domain length is anomalous.',
    children: lenChildren,
  });

  // --- 8. Punycode / Homograph Detection ---
  const hasPunycode = hostname.includes('xn--');
  const nonAscii = hostname.match(/[^\x00-\x7F]/g) || [];
  let punyScore = 0;
  const punyChildren = [];
  if (hasPunycode) {
    punyScore += 20;
    punyChildren.push({ id: 'puny-xn', name: 'Punycode prefix "xn--" detected', severity: 'critical', score: 20,
      description: 'Domain uses internationalized encoding — may display differently than the actual domain.', children: [] });
  }
  if (nonAscii.length > 0) {
    punyScore += 20;
    punyChildren.push({ id: 'puny-chars', name: `${nonAscii.length} non-ASCII character(s)`, severity: 'critical', score: 20,
      description: `Characters: ${nonAscii.map(c => `"${c}" (U+${c.charCodeAt(0).toString(16).toUpperCase()})`).join(', ')}`,
      children: nonAscii.map((c, i) => ({
        id: `puny-char-${i}`, name: `"${c}" — U+${c.charCodeAt(0).toString(16).toUpperCase()}`,
        severity: 'critical', score: 5,
        description: `Non-ASCII character that may visually impersonate a Latin letter.`, children: [],
      })),
    });
  }
  totalScore += Math.min(punyScore, 25);
  tree.push({
    id: 'punycode', name: 'Punycode / Homograph Detection', severity: punyScore > 0 ? 'critical' : 'safe',
    score: Math.min(punyScore, 25),
    description: 'Detects internationalized domain name attacks and non-ASCII characters.',
    children: punyChildren.length > 0 ? punyChildren : [{
      id: 'puny-none', name: 'No homograph indicators', severity: 'safe', score: 0,
      description: 'Domain uses only standard ASCII characters.', children: [],
    }],
  });

  // --- 9. Protocol & TLD ---
  let miscScore = 0;
  const miscChildren = [];
  if (protocol === 'http:') {
    miscScore += 10;
    miscChildren.push({ id: 'misc-http', name: 'No HTTPS', severity: 'high', score: 10,
      description: 'Connection is not encrypted.', children: [] });
  }
  if (SUSPICIOUS_TLDS.includes(tld)) {
    miscScore += 10;
    miscChildren.push({ id: 'misc-tld', name: `Suspicious TLD "${tld}"`, severity: 'high', score: 10,
      description: `TLD "${tld}" is commonly associated with phishing and spam.`, children: [] });
  }
  totalScore += miscScore;
  if (miscChildren.length > 0) {
    tree.push({
      id: 'misc', name: 'Protocol & TLD', severity: miscScore >= 15 ? 'critical' : miscScore > 0 ? 'high' : 'safe',
      score: miscScore, description: 'Additional protocol and TLD signals.',
      children: miscChildren,
    });
  }

  // --- Final ---
  totalScore = Math.min(totalScore, 100);
  let verdict;
  if (totalScore >= 60) verdict = 'High Risk';
  else if (totalScore >= 30) verdict = 'Suspicious';
  else verdict = 'Likely Safe';

  return { riskScore: totalScore, verdict, hostname, signals: tree };
}

// ===============================
// 3. PHISHING LINGUISTICS ENGINE
// ===============================

const LINGUISTICS_FEATURES = {
  urgencyPressure: {
    name: 'Urgency Pressure Tactics',
    maxScore: 20,
    patterns: [
      { text: 'urgent', weight: 3 }, { text: 'immediately', weight: 3 }, { text: 'act now', weight: 4 },
      { text: 'expires today', weight: 3 }, { text: 'last chance', weight: 3 }, { text: 'final warning', weight: 4 },
      { text: 'within 24 hours', weight: 4 }, { text: 'within 48 hours', weight: 3 },
      { text: 'time is running out', weight: 3 }, { text: 'limited time', weight: 2 },
      { text: 'don\'t delay', weight: 2 }, { text: 'right away', weight: 2 },
      { text: 'before it\'s too late', weight: 3 }, { text: 'failure to respond', weight: 4 },
      { text: 'account will be closed', weight: 4 }, { text: 'account suspended', weight: 4 },
      { text: 'access will be revoked', weight: 4 }, { text: 'must respond', weight: 3 },
    ],
  },
  authorityImpersonation: {
    name: 'Authority Impersonation',
    maxScore: 20,
    patterns: [
      { text: 'official notice', weight: 4 }, { text: 'from the desk of', weight: 3 },
      { text: 'it department', weight: 3 }, { text: 'security team', weight: 3 },
      { text: 'compliance department', weight: 3 }, { text: 'board of directors', weight: 3 },
      { text: 'ceo', weight: 2 }, { text: 'chief executive', weight: 3 },
      { text: 'legal department', weight: 3 }, { text: 'human resources', weight: 2 },
      { text: 'we have detected', weight: 3 }, { text: 'our records indicate', weight: 3 },
      { text: 'your account has been', weight: 3 }, { text: 'we noticed', weight: 2 },
      { text: 'as per our policy', weight: 3 }, { text: 'internal audit', weight: 3 },
    ],
  },
  credentialHarvesting: {
    name: 'Credential Harvesting Language',
    maxScore: 25,
    patterns: [
      { text: 'verify your identity', weight: 5 }, { text: 'confirm your account', weight: 4 },
      { text: 'update your information', weight: 3 }, { text: 'enter your password', weight: 5 },
      { text: 'confirm your password', weight: 5 }, { text: 'social security', weight: 5 },
      { text: 'credit card', weight: 5 }, { text: 'bank account', weight: 5 },
      { text: 'card number', weight: 5 }, { text: 'cvv', weight: 5 }, { text: 'pin number', weight: 5 },
      { text: 'routing number', weight: 5 }, { text: 'login credentials', weight: 5 },
      { text: 'sign-in details', weight: 4 }, { text: 'verification code', weight: 4 },
      { text: 'one-time password', weight: 4 }, { text: 'ssn', weight: 5 },
    ],
  },
  rewardThreatBait: {
    name: 'Reward or Threat Bait',
    maxScore: 15,
    patterns: [
      { text: 'you\'ve won', weight: 4 }, { text: 'congratulations', weight: 3 },
      { text: 'prize', weight: 3 }, { text: 'reward', weight: 2 }, { text: 'free gift', weight: 4 },
      { text: 'exclusive offer', weight: 2 }, { text: 'selected winner', weight: 4 },
      { text: 'legal action', weight: 4 }, { text: 'prosecution', weight: 4 },
      { text: 'warrant', weight: 3 }, { text: 'lawsuit', weight: 3 },
      { text: 'police', weight: 2 }, { text: 'arrest', weight: 3 },
      { text: 'penalty', weight: 2 }, { text: 'fine of', weight: 3 },
    ],
  },
  punctuationAnomalies: {
    name: 'Punctuation Anomalies',
    maxScore: 10,
    customAnalyzer: true,
  },
  grammarIrregularities: {
    name: 'Grammar Irregularities',
    maxScore: 10,
    patterns: [
      { text: 'kindly do the needful', weight: 3 }, { text: 'please to', weight: 2 },
      { text: 'do the needful', weight: 3 }, { text: 'revert back', weight: 2 },
      { text: 'kindly revert', weight: 2 }, { text: 'dear sir/madam', weight: 2 },
      { text: 'dear valued customer', weight: 3 }, { text: 'dear user', weight: 2 },
      { text: 'dear account holder', weight: 3 }, { text: 'we are hereby', weight: 2 },
      { text: 'please be informed', weight: 2 }, { text: 'this is to inform', weight: 2 },
      { text: 'irregularities in your', weight: 3 }, { text: 'your account have been', weight: 3 },
      { text: 'has been compromise', weight: 3 }, { text: 'click below link', weight: 3 },
    ],
  },
};

function analyzePhishingLinguistics(text) {
  if (!text || text.trim().length < 20) {
    return { riskScore: 0, verdict: 'Insufficient Text', features: [], totalMatches: 0 };
  }

  const lower = text.toLowerCase();
  const features = [];
  let totalScore = 0;

  for (const [key, config] of Object.entries(LINGUISTICS_FEATURES)) {
    if (config.customAnalyzer && key === 'punctuationAnomalies') {
      // Custom punctuation analysis
      const exclamations = (text.match(/!/g) || []).length;
      const questions = (text.match(/\?/g) || []).length;
      const allCapsWords = text.split(/\s+/).filter(w => w.length > 2 && w === w.toUpperCase() && /[A-Z]/.test(w));
      const ellipses = (text.match(/\.{3,}/g) || []).length;
      const matches = [];
      let featScore = 0;
      if (exclamations > 3) { featScore += 3; matches.push({ text: `${exclamations} exclamation marks`, weight: 3 }); }
      if (allCapsWords.length > 3) { featScore += 4; matches.push({ text: `${allCapsWords.length} ALL-CAPS words: ${allCapsWords.slice(0, 5).join(', ')}`, weight: 4 }); }
      if (exclamations > 0 && questions > 0 && (exclamations + questions) > 5) { featScore += 3; matches.push({ text: 'Mixed excessive punctuation', weight: 3 }); }
      if (ellipses > 1) { featScore += 2; matches.push({ text: `${ellipses} ellipses (...)`, weight: 2 }); }
      const capped = Math.min(featScore, config.maxScore);
      totalScore += capped;
      features.push({
        key, name: config.name, score: capped, maxScore: config.maxScore,
        matches, matchCount: matches.length,
        description: matches.length > 0 ? 'Unusual punctuation patterns detected.' : 'Punctuation appears normal.',
      });
      continue;
    }

    const matches = [];
    let featScore = 0;
    for (const p of config.patterns) {
      if (lower.includes(p.text)) {
        featScore += p.weight;
        matches.push(p);
      }
    }
    const capped = Math.min(featScore, config.maxScore);
    totalScore += capped;
    features.push({
      key, name: config.name, score: capped, maxScore: config.maxScore,
      matches, matchCount: matches.length,
      description: matches.length > 0
        ? `Detected ${matches.length} indicator(s) for ${config.name.toLowerCase()}.`
        : `No ${config.name.toLowerCase()} indicators found.`,
    });
  }

  totalScore = Math.min(totalScore, 100);
  let verdict;
  if (totalScore >= 60) verdict = 'High Phishing Risk';
  else if (totalScore >= 30) verdict = 'Suspicious';
  else verdict = 'Likely Legitimate';

  return {
    riskScore: totalScore,
    verdict,
    features,
    totalMatches: features.reduce((s, f) => s + f.matchCount, 0),
  };
}

// ===============================
// EXPORTS
// ===============================

export {
  TARGET_BRANDS,
  generateAttackerUrls,
  analyzeDeepDomain,
  analyzePhishingLinguistics,
};
